import { useRef, useCallback, useEffect } from "react"

function useDebounceCallback<T extends (...args: any[]) => any>(
  callback: T,
  delay: number = 300,
  deps?: React.DependencyList,
): T {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const callbackRef = useRef(callback)

  useEffect(
    () => {
      callbackRef.current = callback
    },
    deps ? [...deps] : [callback],
  )

  const debouncedCallback = useCallback(
    ((...args: Parameters<T>) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      timeoutRef.current = setTimeout(() => {
        callbackRef.current(...args)
      }, delay)
    }) as T,
    [delay],
  )

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    debouncedCallback()
  }, [debouncedCallback, ...(deps ?? [])])

  return debouncedCallback
}

export default useDebounceCallback
